/**
 * SubscribeButton + SubscribeLink (SW-8538 Phase B).
 *
 * Replaces the previous button/link wrappers in `Utils.js`. Lives in `.tsx`
 * so Vitest can import it for unit tests (per CLAUDE.md gotcha — JSX in
 * `.js` files is unimportable from Vitest).
 *
 * Behavior:
 *
 *   - EU + !hasPaid → open the VAT modal via the Zustand store, with
 *     `createSession` as the `onComplete` continuation. The modal collects
 *     VAT info, POSTs to /api/vat-customer-update, and on success calls
 *     `createSession` (legacy hosted Stripe Checkout).
 *
 *   - EU + hasPaid (grandfathered, paid before VAT collection rolled out)
 *     → call `createSession` directly. No need to re-collect VAT info we
 *     already have on the Stripe customer.
 *
 *   - Non-EU → call `createSession` directly.
 *
 *   - Geolocation still loading → suppress the click. The user's next click
 *     after geo settles will route correctly. We deliberately do NOT
 *     fall through to a Stripe-direct path here — that would silently bypass
 *     VAT for an EU user we haven't classified yet.
 */
import * as Sentry from "@sentry/react";
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore — Auth.js is a JS file with mixed exports.
import Auth from "../Auth";
import useVatCountry from "../hooks/useVatCountry";
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore — Zustand store is a JS file.
import { useSudowriteStore } from "../stores/sudowriteStore";
import { useVatCheckoutStore } from "../stores/vatCheckoutStore";

type SubscribeProps = {
  children: React.ReactNode;
  origin?: string;
};

/**
 * Legacy hosted-Stripe-checkout creator. Identical to the implementation
 * that lived on master before SW-8538. Keep this co-located with the
 * wrappers so we can pass it as the `onComplete` continuation in one place.
 */
export async function createSession(): Promise<void> {
  try {
    const result = await Auth.fetchWithIdToken("/checkout/createSession", {});
    if (!result?.sessionId) {
      throw new Error("Legacy checkout session missing sessionId");
    }
    // window.Stripe is loaded globally by the marketing/index.html shell.
    const stripe = (window as unknown as { Stripe: (k: string) => { redirectToCheckout: (opts: { sessionId: string }) => Promise<unknown> } }).Stripe(
      process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY as string
    );
    await stripe.redirectToCheckout({ sessionId: result.sessionId });
  } catch (err) {
    Sentry.captureException(err);
    throw err;
  }
}

/**
 * Decide what should happen when the user clicks Subscribe. Encapsulates
 * the EU-gate + hasPaid + isLoading branching so the button + link both
 * delegate to one function. Exported only for tests; production callers
 * should use SubscribeButton / SubscribeLink.
 */
function useHandleSubscribeClick(origin?: string): () => void {
  const { isEU, isLoading } = useVatCountry();
  const subscription = useSudowriteStore(
    (state: { subscription: { hasPaid: boolean } | null }) => state.subscription
  );
  const openVatModal = useVatCheckoutStore((state: any) => state.openVatModal);

  return () => {
    // Suppress mid-load clicks. Don't fall through to a Stripe-direct path
    // that would skip VAT for an EU user we haven't classified yet.
    if (isLoading) return;

    const hasPaid = !!subscription?.hasPaid;

    if (isEU && !hasPaid) {
      // EU + first-time paying user — collect VAT info, then create the
      // Stripe session.
      openVatModal({ onComplete: createSession, origin: origin || null });
      return;
    }

    // Non-EU, OR EU + grandfathered paid customer → straight to Stripe.
    void createSession();
  };
}

export function SubscribeButton({ children, origin }: SubscribeProps) {
  const handleClick = useHandleSubscribeClick(origin);
  return (
    <button
      onClick={(e) => {
        e.preventDefault();
        handleClick();
      }}
    >
      {children}
    </button>
  );
}

export function SubscribeLink({ children, origin }: SubscribeProps) {
  const handleClick = useHandleSubscribeClick(origin);
  return (
    <a
      onClick={(e) => {
        e.preventDefault();
        handleClick();
      }}
    >
      {children}
    </a>
  );
}
